import AdapterDateFns from "@material-ui/lab/AdapterDateFns";
import { es } from "date-fns/locale";
import { DatePicker, LocalizationProvider } from "@material-ui/lab";
import { useState } from "react";
import {
  Autocomplete,
  Box,
  Chip,
  Collapse,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@material-ui/core";
import { ExpandLess, ExpandMore, FilterList, Search } from "@material-ui/icons";
import { makeStyles } from "@material-ui/styles";
import { ESTADOS_TAREA, TIPOS_TAREA } from "../hooks/useTareasFilters";
import ActiveFiltersChips from "./ActiveFiltersChips";
import SucursalFilter from "src/components/estadisticas/sucursal-filter";

// ==================== ESTILOS ====================

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1.5, 2),
    backgroundColor: "#FAFAFA",
    borderRadius: 12,
    cursor: "pointer",
    transition: "background-color 0.15s ease",
    border: "1px solid #E0E0E0",
    "&:hover": {
      backgroundColor: "#F5F5F5",
    },
  },
  headerExpanded: {
    borderRadius: "12px 12px 0 0",
    borderBottom: "none",
  },
  headerTitle: {
    fontWeight: 600,
    color: "#424242",
  },
  filterCount: {
    backgroundColor: "#1976D2",
    color: "#FFFFFF",
    fontWeight: 500,
    fontSize: "0.75rem",
  },
  content: {
    padding: theme.spacing(2.5),
    border: "1px solid #E0E0E0",
    borderTop: "none",
    borderRadius: "0 0 12px 12px",
    backgroundColor: "#FFFFFF",
  },
  sectionLabel: {
    fontSize: "0.75rem",
    color: "#757575",
    fontWeight: 500,
    marginBottom: theme.spacing(0.5),
    display: "block",
  },
  estadoChip: {
    cursor: "pointer",
    transition: "all 0.15s ease",
    fontWeight: 500,
    "&:hover": {
      transform: "scale(1.03)",
    },
  },
  searchField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      backgroundColor: "#FAFAFA",
      transition: "background-color 0.15s ease",
      "&:hover": {
        backgroundColor: "#F5F5F5",
      },
      "&.Mui-focused": {
        backgroundColor: "#FFFFFF",
      },
    },
  },
  selectField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
    },
  },
  dateField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
    },
  },
}));

/**
 * Panel de filtros colapsable para tareas
 */
export default function TareasFilterPanel({
  filtros,
  updateFiltro,
  toggleEstado,
  limpiarFiltros,
  removerFiltro,
  filtrosActivos,
  hayFiltrosActivos,
  opcionesClientes,
  opcionesEmpleados,
  isAdmin,
  rutEmpleado,
  showFilters = {
    busqueda: true,
    sucursales: true,
    estados: true,
    tipoTarea: true,
    cliente: true,
    empleado: true,
    fechas: true,
    soloEncargado: false,
  },
  // ─── Estados a mostrar en los chips (por defecto todos) ─────────────
  estadosFiltrados = ESTADOS_TAREA,
}) {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(true);

  const handleToggleExpand = () => {
    setExpanded((prev) => !prev);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} locale={es}>
      <Box className={classes.container}>
        {/* Header del panel */}
        <Box
          className={`${classes.header} ${
            expanded ? classes.headerExpanded : ""
          }`}
          onClick={handleToggleExpand}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <FilterList sx={{ color: "#757575", fontSize: 20 }} />
            <Typography variant="subtitle1" className={classes.headerTitle}>
              Filtros
            </Typography>
            {filtrosActivos > 0 && (
              <Chip
                size="small"
                label={`${filtrosActivos} activo${
                  filtrosActivos > 1 ? "s" : ""
                }`}
                className={classes.filterCount}
              />
            )}
          </Stack>
          <IconButton size="small" onClick={(e) => e.stopPropagation()}>
            {expanded ? (
              <ExpandLess sx={{ color: "#757575" }} />
            ) : (
              <ExpandMore sx={{ color: "#757575" }} />
            )}
          </IconButton>
        </Box>

        {/* Contenido colapsable */}
        <Collapse in={expanded}>
          <Box className={classes.content}>
            <Grid container spacing={2.5}>
              {/* Fila 1: Búsqueda, Sucursales y Estados */}
              {/* BÚSQUEDA - Solo renderiza si showFilters.busqueda es true */}
              {showFilters.busqueda && (
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Buscar tarea o cliente"
                    placeholder="Escriba para buscar..."
                    value={filtros.busqueda}
                    onChange={(e) => updateFiltro("busqueda", e.target.value)}
                    className={classes.searchField}
                    InputProps={{
                      startAdornment: (
                        <Search
                          sx={{ mr: 0.5, color: "#9E9E9E", fontSize: 20 }}
                        />
                      ),
                    }}
                  />
                </Grid>
              )}

              {/* SUCURSALES - Solo renderiza si showFilters.sucursales es true */}
              {showFilters.sucursales && (
                <SucursalFilter
                  rutEmpleado={rutEmpleado}
                  sucursalesSeleccionadas={filtros.sucursales}
                  onChangeSucursales={(nuevasSucursales) =>
                    updateFiltro("sucursales", nuevasSucursales)
                  }
                  tamanio={12}
                  md={3}
                  label=""
                  size="small"
                />
              )}

              {/* ESTADOS - Solo renderiza si showFilters.estados es true */}
              {showFilters.estados && (
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography className={classes.sectionLabel}>
                      Estado
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={0.75}>
                      {estadosFiltrados.map((estado) => {
                        const isSelected = filtros.estados?.includes(
                          estado.value,
                        );
                        return (
                          <Chip
                            key={estado.value}
                            label={estado.label}
                            size="small"
                            color={isSelected ? estado.color : "default"}
                            variant={isSelected ? "filled" : "outlined"}
                            onClick={() => toggleEstado(estado.value)}
                            className={classes.estadoChip}
                          />
                        );
                      })}
                    </Stack>
                  </Box>
                </Grid>
              )}

              {/* Fila 2: Tipo Tarea, Cliente, Empleado */}
              {/* TIPO DE TAREA - Solo renderiza si showFilters.tipoTarea es true */}
              {showFilters.tipoTarea && (
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl
                    fullWidth
                    size="small"
                    className={classes.selectField}
                  >
                    <InputLabel>Tipo de Tarea</InputLabel>
                    <Select
                      value={filtros.tipoTarea}
                      label="Tipo de Tarea"
                      onChange={(e) =>
                        updateFiltro("tipoTarea", e.target.value)
                      }
                    >
                      <MenuItem value="">
                        <em>Todos</em>
                      </MenuItem>
                      {TIPOS_TAREA.map((tipo) => (
                        <MenuItem key={tipo.value} value={tipo.value}>
                          {tipo.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {/* CLIENTE - Solo renderiza si showFilters.cliente es true */}
              {showFilters.cliente && (
                <Grid item xs={12} sm={6} md={isAdmin ? 4 : 5}>
                  <Autocomplete
                    size="small"
                    options={opcionesClientes}
                    getOptionLabel={(option) => option.label || ""}
                    value={filtros.cliente}
                    onChange={(_, newValue) =>
                      updateFiltro("cliente", newValue)
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Cliente"
                        placeholder="Buscar cliente..."
                        className={classes.selectField}
                      />
                    )}
                    noOptionsText="Sin resultados"
                    clearText="Limpiar"
                  />
                </Grid>
              )}

              {/* EMPLEADO - Solo renderiza si showFilters.empleado es true Y isAdmin es true */}
              {showFilters.empleado && isAdmin && (
                <Grid item xs={12} sm={6} md={4}>
                  <Autocomplete
                    size="small"
                    options={opcionesEmpleados}
                    getOptionLabel={(option) => option.label || ""}
                    value={filtros.empleado}
                    onChange={(_, newValue) =>
                      updateFiltro("empleado", newValue)
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Empleado"
                        placeholder="Buscar empleado..."
                        className={classes.selectField}
                      />
                    )}
                    noOptionsText="Sin resultados"
                    clearText="Limpiar"
                  />
                </Grid>
              )}

              {/* MI PARTICIPACIÓN - Solo renderiza si showFilters.soloEncargado es true y el usuario no es administrador */}
              {showFilters.soloEncargado && !isAdmin && (
                <Grid item xs={12} sm={6} md={3}>
                  <Box>
                    <Typography className={classes.sectionLabel}>
                      Mi participación
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={filtros.soloEncargado}
                          onChange={(e) =>
                            updateFiltro("soloEncargado", e.target.checked)
                          }
                        />
                      }
                      label="Solo donde soy encargado"
                    />
                  </Box>
                </Grid>
              )}

              {/* FECHAS - Solo renderiza si showFilters.fechas es true */}
              {showFilters.fechas && (
                <Grid item xs={12}>
                  <Typography className={classes.sectionLabel}>
                    Rangos de fecha
                  </Typography>
                  <Grid container spacing={2}>
                    {/* Fecha Inicio */}
                    <Grid item xs={12} sm={6} md={3}>
                      <DatePicker
                        label="Inicio desde"
                        value={filtros.fechaInicioDesde}
                        onChange={(newValue) =>
                          updateFiltro("fechaInicioDesde", newValue)
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            size="small"
                            className={classes.dateField}
                          />
                        )}
                        clearable
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <DatePicker
                        label="Inicio hasta"
                        value={filtros.fechaInicioHasta}
                        onChange={(newValue) =>
                          updateFiltro("fechaInicioHasta", newValue)
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            size="small"
                            className={classes.dateField}
                          />
                        )}
                        minDate={filtros.fechaInicioDesde || undefined}
                        clearable
                      />
                    </Grid>

                    {/* Fecha Término */}
                    <Grid item xs={12} sm={6} md={3}>
                      <DatePicker
                        label="Término desde"
                        value={filtros.fechaTerminoDesde}
                        onChange={(newValue) =>
                          updateFiltro("fechaTerminoDesde", newValue)
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            size="small"
                            className={classes.dateField}
                          />
                        )}
                        clearable
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <DatePicker
                        label="Término hasta"
                        value={filtros.fechaTerminoHasta}
                        onChange={(newValue) =>
                          updateFiltro("fechaTerminoHasta", newValue)
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            size="small"
                            className={classes.dateField}
                          />
                        )}
                        minDate={filtros.fechaTerminoDesde || undefined}
                        clearable
                      />
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </Grid>

            {/* Chips de filtros activos */}
            <ActiveFiltersChips
              filtros={filtros}
              removerFiltro={removerFiltro}
              limpiarFiltros={limpiarFiltros}
              hayFiltrosActivos={hayFiltrosActivos}
            />
          </Box>
        </Collapse>
      </Box>
    </LocalizationProvider>
  );
}
